<template>
    <div class="base-card__content base-card__zoom base-card__shadow program-content" :class="classesProgramContent">
        <div class="program-content__wrapper">
            <img
                v-if="imageUrl"
                loading="lazy"
                class="program-content__image"
                :class="{'program-content__image_blured': isParentallyRestricted}"
                :alt="title"
                :src="imageUrl">
                <common-icon v-else class="program-content__image" :icon-name="'IconNoPicture'" 
            />

            <div v-show="isRemovedFromFavorite" class="program-content__favorite-label">{{ captionRemoved }}</div>
        </div>
        <div class="program-content__pic" :class="[ inFuture ? 'future': 'playable' ]" />
        <common-icon v-if="isParentallyRestricted" class="program-content__image-lock" :icon-name="'IconLock'" />
        <span v-if="isLive" class="program-content__live">
            {{ live }}
        </span>
        <span class="program-content__track">
            <span :style="{ width: progress + '%' }" />
        </span>
        <span v-if="subImage" class="program-content__channel">
            <img :src="subImage" :alt="channelName" loading="lazy" >
        </span>
        <div class="base-card__shift program-content__description">
            <div class="program-content__name">{{ title }}</div>
            <div class="program-content__time">
                <span v-if="displayTime[0]">{{ displayTime[0] }}</span>
                <span v-if="displayTime[1]" class="program-content__time-capitalize">{{ displayTime[1] }}</span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { type ProgramGuideItem, PlayItem,type Channel } from "@ca.vmedia.tv/domains";
    import { Strings } from "@/locales/types/enums";
    import { type ProgramCardData } from "@/components/Cards/types";
    import { ImgUrlUtils, TimeUtils } from "@/utils";
    import { AppConstants } from "@/common";
    import { useChannelsStore } from "@/stores/Channels";
    import { useApplicationStore } from "@/stores/Application";
    import { useI18n } from "vue-i18n";
    import { useParentControl } from "@/composables/parentControl/useParentControl";
    import { useUserInfoStore } from "@/stores/UserInfo";
    import { storeToRefs } from 'pinia';
    import { LangCodes } from "@/common/types/languages";
    import { useProgramInfo } from "@/composables/useProgramInfo/useProgramInfo";
    import type { AxiosError } from "axios";

    const ApplicationStore = useApplicationStore();
    const {
        getCurrentTime: currentTime,
    } = storeToRefs(ApplicationStore);

    const ChannelsStore = useChannelsStore();
    const {
        getAvailableChannels: allChannels,
    } = storeToRefs(ChannelsStore);
    
    const { hasDeletedIdsFavoritePrograms } = useFavorite();

    const UserInfoStoreModule = useUserInfoStore();
    const {
        isGuest,
    } = storeToRefs(UserInfoStoreModule);

    const props = defineProps({
        cardData: {
            type: Object as PropType<ProgramCardData>,
            required: true
        },
        /**
         * The flag to indicate if the program info should be enabled
         */
        enableProgramInfo: {
            type: Boolean,
            required: false,
            default: false
        },
    });

    const emit = defineEmits(["program-info"]);
    const { HeaderImageWidth, ProgramCardImageWidth, quantityOfMsInOneDay }: typeof AppConstants = AppConstants;
    const { isTimeFormat12 } = useAppConfig();
    const { t, locale } = useI18n();
    const { $domains } = useNuxtApp();
    const live: string = t(Strings.HOME_LIVE).toString();
    const { needsUnlockPin } = useParentControl();
    const subImage: Ref<string | null> = ref(null);
    const captionRemoved: string = t(Strings.CARD_REMOVED).toString();

    const isRemovedFromFavorite: ComputedRef<boolean> = computed(() => hasDeletedIdsFavoritePrograms(`${props.cardData.getId()}:${props.cardData.getContentType()}`));
    const classesProgramContent: ComputedRef<{[key: string]: boolean}> = computed(() => {
        return {
            'program-content_isRemovedFromFavorite': isRemovedFromFavorite.value
        };
    });

    const inFuture = computed((): boolean => {
        const startTime = PlayItem.getStartTime(program.value);
        if (startTime) {
            const timeDiff = TimeUtils.getTime() - startTime.getTime();
            return timeDiff < 0;
        }
        return true;
    });
    const imageUrl = computed((): string => {
        const image = props.cardData.getImage();

        if (!image) {
            return '';
        } else {
            return ImgUrlUtils.src(image, ProgramCardImageWidth);
        }
    });
    const program = computed((): ProgramGuideItem | null => {
        return props.cardData.getProgram();
    });
    const title = computed((): string => {
        return program.value?.name ?? "";
    });
    const channelName = computed((): string => {
        return currentChannel.value && currentChannel.value.name ? currentChannel.value.name : "";
    });
    const currentChannel = computed((): Channel | null => allChannels.value.find(item => item.id === program.value?.recentAirTime?.channelId) ?? null);
    const isParentallyRestricted = computed((): boolean => needsUnlockPin(program.value?.advisory ?? currentChannel.value?.advisory));

    const getSubImage = async () => {
        if (program.value?.subImage) {
            subImage.value = program.value?.subImage
            return;
        }
        try {
            await $domains.imagesRepository.getWhiteChannelImage(program.value?.recentAirTime?.imagesChannel);
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        }
    };

    const startDate = computed((): number | null => PlayItem.getStartTime(program.value)?.getTime() ?? null);
    const progress = computed((): number => {
        if (isGuest.value && PlayItem.isVCloud(props.cardData.getPlayItem(), currentChannel.value)) {
            return 0;
        }

        if (isLive.value) {
            if (startDate.value && program.value?.lengthInS) {
                return Math.floor((currentTime.value - startDate.value) / (program.value?.lengthInS * 10));
            }
            return 0;
        }
        
        if(program.value && program.value?.streamPosition && program.value?.lengthInS) {
            return Math.floor((program.value?.streamPosition / program.value?.lengthInS) * 100);
        }

        return 0;
    });
    const isLive = computed((): boolean => PlayItem.isLive(props.cardData.getPlayItem()));
    const displayTime = computed((): string[] => {
        if (!program.value) {
            return [""];
        }

        const startTime = PlayItem.getStartTime(program.value);

        if (!startTime) {
            return [""];
        }
        
        const data = TimeUtils.getMoment(locale.value, startTime.getTime());
        if (props.cardData.isTimeOnly()) {
            const timeFormatIsTimeOnly = isTimeFormat12 ? ' hh:mm A' : ' HH:mm';
            return [data.format(timeFormatIsTimeOnly)];
        }

        if (PlayItem.isLive(program.value)) {
            return [t(Strings.CARD_NOW_PLAYING).toString()];//'Now Playing'
        }
                
        const today = TimeUtils.newDate();
        today.setHours(0, 0, 0, 0);

        const compareDate = new Date(startTime.getFullYear(), startTime.getMonth(), startTime.getDate());
        const diff = today.getTime() - compareDate.getTime();
        const dateType = locale.value === LangCodes.FR ? 'D MMM' : 'MMM D';
        const timeFormat = isTimeFormat12 ? ` hh:mm A | ${dateType}` : ` HH:mm | ${dateType}`;
        
        let dayWeek = data.format('dddd') + ' ' + t(Strings.CARD_AT).toString();
        if (diff === 0) {
            dayWeek = t(Strings.CARD_TODAY_AT).toString();     //'Today'
        } else if (diff == (quantityOfMsInOneDay)) {
            dayWeek = t(Strings.CARD_YESTERDAY_AT).toString(); //'Yesterday'
        } else if (diff == -(quantityOfMsInOneDay)) {
            dayWeek = t(Strings.CARD_TOMORROW_AT).toString(); //'Tomorrow'
        } else {
            dayWeek = dayWeek[0].toUpperCase() + dayWeek.slice(1);
        }

        return [dayWeek, data.format(timeFormat)];
    });

    onMounted(() => {
        void getSubImage();
    });

    if (props.enableProgramInfo) {
        const createProgramInfo = (currentProgram: ProgramGuideItem | null, channel: Channel | null): void => {
            if(currentProgram === null || channel === null) {
                return;
            }

            const img = ImgUrlUtils.src(currentProgram.image, HeaderImageWidth);
            const programInfo = useProgramInfo(currentProgram, channel, img);
            
            emit("program-info", programInfo);
        };

        createProgramInfo(program.value, currentChannel.value);
    }
</script>

<style lang="scss">
    @import url("styles.scss");
</style>